import dayjs, { Dayjs } from 'dayjs';
import { DenmarkArea, denmarkAreasMap } from './types';
import { WeatherAPIResponse } from './interfaces';
import { BarchartObject } from '../charts/interfaces';
import { GroupBy, TimeGroupingInterval } from '../../types';
import { severityOptions } from '@Iot-Bee/standard-web-library';

function convertToUTCString(date: Dayjs): string {
  return date.utc().toISOString();
}

function checkIfDateInTimeRange(timeRange: [number, number], date: Dayjs): boolean {
  // Calculate the number of minutes past midnight for the given date
  const minutesSinceMidnight = date.hour() * 60 + date.minute();

  // Check if the calculated minutes fall within the range
  return minutesSinceMidnight >= timeRange[0] && minutesSinceMidnight <= timeRange[1];
}

export function extractInsideOutsideValues(
  dateDict: Record<string, { inside: number | null; outside: number | null }>
): { insideValues: number[]; outsideValues: number[] } {
  const insideValues: number[] = [];
  const outsideValues: number[] = [];

  Object.values(dateDict).forEach(({ inside, outside }) => {
    insideValues.push(inside!); // Use 0 if the value is null
    outsideValues.push(outside!); // Use 0 if the value is null
  });

  return { insideValues, outsideValues };
}

export function generateWeatherAPIURL(selectedTemperatureArea: string, fromDate: Dayjs, toDate: Dayjs): string {
  const url =
    process.env.REACT_APP_WEATHER_URL +
    `?limit=10000&parameterId=mean_temp&sortorder=from,DESC&stationId=${denmarkAreasMap[selectedTemperatureArea as keyof typeof denmarkAreasMap].id}&datetime=${convertToUTCString(fromDate)}/${convertToUTCString(toDate)}&timeResolution=hour&api-key=${process.env.REACT_APP_WEATHER_API_KEY}`;
  return url;
}

export function adjustTempDataToKWHDates(
  data: WeatherAPIResponse,
  chartData: BarchartObject,
  timePeriod: TimeGroupingInterval,
): Record<string, number> {
  // Initialize a dictionary to store the sum and count for each date
  const dateDict: Record<string, { sum: number; count: number }> = chartData.categories.reduce(
    (acc, date) => {
      acc[date] = { sum: 0, count: 0 };
      return acc;
    },
    {} as Record<string, { sum: number; count: number }>
  );

  data.features.forEach((feature) => {
    const featureDate = dayjs(feature.properties.from);
    const groupKey =
      timePeriod === 'week'
        ? 'W-' + String(featureDate.week()) // Use .week() for "Week" grouping
        : featureDate.format(
            {
              day: 'YYYY-MM-DD',
              month: 'YYYY-MM',
              year: 'YYYY',
            }[timePeriod]
          );

    if (dateDict[groupKey]) {
      dateDict[groupKey].sum += feature.properties.value;
      dateDict[groupKey].count += 1;
    }
  });

  // calculate the mean value for each bin
  const meanDict: Record<string, number> = {};
  for (const [key, value] of Object.entries(dateDict)) {
    if (value.count > 0) {
      meanDict[key] = value.sum / value.count;
    } else { // handle division by zero
      meanDict[key] = 0; 
    }
  }

  return meanDict;
}

export async function fetchWeatherData(
  url: string,
  openSnackbar: (type: severityOptions, message: string) => void
): Promise<WeatherAPIResponse> {
  const res: WeatherAPIResponse = {
    features: [],
  };

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response) {
      const data: WeatherAPIResponse = await response.json();
      if (data.features.length >= 10000) {
        openSnackbar('info', 'The maximum limit of 10,000 temperature data points has been exceeded!');
      }
      return data;
    }
    return res;
  } catch (error) {
    console.error('Error fetching data:', error);
    return res;
  }
}

export function loadSessionStorageValues() {
  const storedTimePeriod = sessionStorage.getItem('timePeriod');
  const storedGroupBy = sessionStorage.getItem('groupBy');
  const storedShowTemperature = sessionStorage.getItem('showTemperature');
  const storedDenmarkArea = sessionStorage.getItem('denmarkArea');

  let timePeriod: TimeGroupingInterval | null = null;
  let groupBy: GroupBy | null = null;
  let showTemperature: boolean | null = null;
  let denmarkArea: DenmarkArea | null = null;

  let timePeriodLoaded = false;
  let groupByLoaded = false;
  let temperatureLoaded = false;
  let areaLoaded = false;

  if (storedTimePeriod) {
    try {
      timePeriod = storedTimePeriod as TimeGroupingInterval;
    } catch (e) {
      console.error('Error parsing timePeriod from sessionStorage - using defaults', e);
    } finally {
      timePeriodLoaded = true;
    }
  } else {
    timePeriodLoaded = true;
  }

  if (storedGroupBy) {
    try {
      groupBy = storedGroupBy as GroupBy;
    } catch (e) {
      console.error('Error parsing groupBy from sessionStorage - using defaults', e);
    } finally {
      groupByLoaded = true;
    }
  } else {
    groupByLoaded = true;
  }

  if (storedShowTemperature) {
    try {
      showTemperature = JSON.parse(storedShowTemperature);
    } catch (e) {
      console.error('Error parsing showTemperature from sessionStorage - using defaults', e);
    } finally {
      temperatureLoaded = true;
    }
  } else {
    temperatureLoaded = true;
  }

  if (storedDenmarkArea) {
    try {
      denmarkArea = storedDenmarkArea as DenmarkArea;
    } catch (e) {
      console.error('Error parsing denmarkArea from sessionStorage - using defaults', e);
    } finally {
      areaLoaded = true;
    }
  } else {
    areaLoaded = true;
  }

  return {
    timePeriod,
    groupBy,
    showTemperature,
    denmarkArea,
    sessionStorageLoaded: timePeriodLoaded && groupByLoaded && temperatureLoaded && areaLoaded,
  };
}
